import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpParams } from '@angular/common/http';
import { AnalyticsService } from './analytics.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);

  /** Temps restant avant expiration du token (en secondes), null si pas de token ou non décodable */
  readonly tokenExpiresIn: WritableSignal<number | null> = signal(null);

  logout(): void {
    // beacon : la requête part même si la page est redirigée juste après
    this.analyticsService.track('logout', { transport_type: 'beacon' });
    localStorage.removeItem('user_access_token');
    this.tokenExpiresIn.set(null);
    this.redirectToLogin();
  }

  redirectToLogin(): void {
    let params: HttpParams = new HttpParams()
      .set('client_id', '53') // le client ID du site-web sur login.kinomap.com
      .set('redirect_uri', window.location.href)
      .set('state', Math.random().toString(36).substring(2, 15))
      .set('lang', localStorage.getItem('lang') || 'en');

    if (window.location.href.includes('beta.kinomap.com')) {
      params = params.set('client_id', '54'); // le client ID du site-web sur beta.kinomap.com
    }

    const oauthUrl: string = `${environment.oAuthUrl}?${params.toString()}`;
    window.location.href = oauthUrl;
  }

  extractTokenFromHash(): string | null {
    const hash: string = window.location.hash;
    if (hash && hash.includes('?token=')) {
      const paramsString: string = hash.split('?')[1];
      if (paramsString) {
        const params: URLSearchParams = new URLSearchParams(paramsString);
        return params.get('token');
      }
    }
    return null;
  }

  /**
   * Décode un JWT et retourne les secondes restantes avant expiration.
   * Retourne null si le token est invalide ou ne contient pas de champ `exp`.
   */
  decodeTokenExpiration(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload?.exp) return null;
      return Math.floor(payload.exp - Date.now() / 1000);
    } catch {
      return null;
    }
  }

  loginOauth(token: string): void {
    if (token && token !== '') {
      localStorage.setItem('user_access_token', token);
      const url: URL = new URL(window.location.href);
      url.searchParams.delete('token');

      if (url.hash && url.hash.includes('token=')) {
        const hash: string = url.hash.substring(1);
        const [hashPath, hashQuery]: string[] = hash.split('?', 2);

        if (hashQuery) {
          const params: URLSearchParams = new URLSearchParams(hashQuery);
          params.delete('token');
          const newHash: string =
            params.toString().length > 0 ? `#${hashPath}?${params.toString()}` : `#${hashPath}`;
          url.hash = newHash;
        } else {
          url.hash = `#${hashPath}`;
        }
      }
      window.history.replaceState({}, document.title, url.toString());
      this.analyticsService.markLoginPending();
      window.location.reload();
    } else {
      throw new Error('invalid token');
    }
  }
}
